import type { AssistantCommandView } from "@opus-dt/shared-types";

// Chaves de query afetadas pelo efeito de cada comando ACTION (tabela viva do CLAUDE.md). Espelha a
// invalidação das mutações das telas (useAcknowledgeAlarm, usePauseProduction, useCreateMachine…).
// Extraído de hooks.ts (guardrail max-lines). Consumido pelo onSuccess do useConfirmCommand.
export function effectKeys(command: AssistantCommandView): unknown[][] {
  const args = command.argsJson;
  const lineId = args.lineId;
  const machineId = args.machineId;
  const deviceId = args.deviceId;
  switch (command.name) {
    case "simulation.run":
      return [["simulations"], ["scenarios", args.scenarioId]];
    case "simulation.suggestImprovements":
      return [["auto-batches"], ["simulations"]];
    case "scenario.create":
      return [["scenarios"]];
    case "simulation.cancelBatch":
      return [["auto-batches"], ["auto-batches", args.batchId]];
    // Operação — reusa as chaves das mutações das telas.
    case "alarm.acknowledge":
    case "alarm.resolve":
    case "alarm.markFalse":
      return [["alarms"], ["line-alarms"], ["attention"]];
    case "machineEvent.acknowledge":
    case "machineEvent.justify":
      return [["machine-events"], ["events"], ["attention"], ["lines"], ["oee"]];
    // A run ativa é keyed ["production","active",lineId] (features/production/hooks.ts); a lista de
    // eventos é ["lines",lineId,"events",…] (coberta pelo prefixo ["lines",lineId]). NÃO usar
    // ["production",lineId]/["events",lineId]: não casam por prefixo e não invalidam nada.
    case "production.pause":
    case "production.resume":
      return [
        ["production", "active", lineId],
        ["lines", lineId],
        ["oee", lineId],
      ];
    case "production.finish":
      return [
        ["production", "active", lineId],
        ["lines", lineId],
        ["lines"],
        ["oee", lineId],
        ["dashboard"],
      ];
    case "production.recordEntry":
      return [
        ["production", "active", lineId],
        ["lines", lineId],
        ["oee", lineId],
      ];
    case "production.registerLoss":
      return [
        ["production", "active", lineId],
        ["lines", lineId],
        ["machines", lineId],
        ["oee", lineId],
      ];
    case "production.updatePlannedTimes":
      return [
        ["production", "active", lineId],
        ["lines", lineId],
        ["lines"],
        ["oee", lineId],
        ["dashboard"],
      ];
    case "productionOrder.create":
      return [["production-orders"], ["lines", lineId]];
    case "productionOrder.update":
    case "productionOrder.cancel":
      return [["production-orders"], ["production-orders", args.orderId]];
    // Parâmetros & apontamentos — espelha useCreateMachine/useDeleteParameter/
    // useRegisterStatusEntry e a UNIÃO de useRegisterParameterEntry + useRegisterProductionEntry
    // (mesmo endpoint serve medição comum e contador de produção — over-invalidação aceita).
    case "parameter.create":
    case "parameter.update":
      return [
        ["parameters", machineId],
        ["machine", machineId],
        // Spec 078 — papel visual/limites mudam a cena da linha
        ["scene"],
      ];
    case "parameter.delete":
      return [
        ["parameters", machineId],
        ["machine", machineId],
        ["sensors", machineId],
      ];
    case "parameter.recordEntry":
      return [
        ["parameters", machineId],
        ["machine", machineId],
        ["machines", lineId],
        ["lines", lineId],
        ["events", lineId],
        ["oee", lineId],
        ["production", "active", lineId],
      ];
    case "machine.recordStatus":
      return [
        ["machine", machineId],
        ["machines", lineId],
        ["lines", lineId],
        ["events", lineId],
        ["oee", lineId],
      ];
    // Config de relações — espelha useUpsertCompatibility / useSetSimilarMachine.
    case "machine.setCompatibility":
      return [
        ["compatibility", lineId],
        ["lines", lineId],
        // O perfil por par entra na resolução ao vivo do preview do sandbox.
        ["scenarios"],
      ];
    case "machine.setSimilar":
      return [
        ["machines", lineId],
        ["machine", machineId],
      ];
    // Ajuste de parâmetros de simulação do cenário — como useSaveScenarioStructure.
    case "scenario.setMachineSimParams":
      return [["scenarios", args.scenarioId], ["simulations"]];
    // Admin de dispositivos — espelha useUpdateDevice / useCreate|DeleteCard /
    // useCreateSensorPosition / useUpdateSensor (sensor vinculado aparece na sidebar da máquina).
    case "device.update":
      return [["devices"], ["devices", deviceId]];
    case "card.create":
    case "card.update":
    case "card.delete":
      return [["cards", deviceId], ["devices"]];
    case "sensor.create":
    case "sensor.update":
    case "sensor.delete":
      return [["cards", deviceId], ["devices", deviceId], ["sensors"], ["lines"], ["parameters"]];
    // Estrutura — espelha as invalidações das mutações das telas (useCreateMachine, useCreateDevice…).
    case "line.create":
    case "line.delete":
      return [["lines"]];
    case "line.update":
      return [["lines", lineId], ["lines"]];
    case "machine.create":
    case "machine.update":
    case "machine.delete":
      return [
        ["lines", lineId],
        ["machines", lineId],
        ["lineLayout", lineId],
        ["scene", lineId], // Spec 078
      ];
    case "machine.duplicate":
      return [
        ["lines", lineId],
        ["lines", lineId, "machines"],
      ];
    case "product.create":
    case "product.update":
    case "product.delete":
      return [["products"]];
    case "device.create":
    case "device.rotateKey":
    case "device.delete":
      return [["devices"]];
    case "sensor.linkToParameter":
    case "sensor.unlink":
      return [["sensors"], ["lines"], ["parameters"]];
    default:
      return [];
  }
}
